import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { useDateLocalStorageValue } from '@wix/da-react-context/pkg/compliantLocalStorage/hooks';
import BoostRootModal from '../../ActionBar/buttons/BoostButton/modals/BoostRootModal';
import { isDeviationAuthor } from '../../DeviationViews/_helpers';
import { getIsPostPublish } from '../redux/selectors';

export function useBoostModalOnLoad(deviation?: PapiDeviation) {
  const { hasCoreMembership, user } = useContext(SessionContext);
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [lastBoostPromoSeen, setLastBoostPromoSeen] = useDateLocalStorageValue(
    CompliantLocalStorageKeys.BOOST_PROMO_LAST_SEEN
  );
  const isPostPublish = useSelector(getIsPostPublish);

  const hasLoaded = useRef(false);
  useEffect(() => {
    if (hasLoaded.current || !deviation) {
      return;
    }

    // Show boost modal when all conditions are met:
    // - Flag is enabled
    // - We were redirected to devpage after submitting a deviation
    // - User is logged in and has Core+ membership
    // - User owns deviation and deviation is boostable
    // - User hasn't used any free boosts (freeBoostsAllowed === freeBoostsRemaining)
    // - Modal hasn't been shown in the last 24 hours
    if (isPostPublish && hasCoreMembership) {
      const isOwner = isDeviationAuthor(deviation, user as PapiUser);
      const { isBoostable, boostRestrictions } = deviation.extended ?? {};
      const { freeBoostsRemaining = 0, freeBoostsAllowed = 0 } =
        boostRestrictions ?? {};
      const isModalShownInLast24Hours =
        lastBoostPromoSeen &&
        new Date().getTime() - lastBoostPromoSeen.getTime() < 24 * 3600 * 1000;
      if (
        isOwner &&
        isBoostable &&
        freeBoostsAllowed > 0 &&
        freeBoostsAllowed === freeBoostsRemaining &&
        !isModalShownInLast24Hours
      ) {
        setIsBoostModalOpen(true);
        setLastBoostPromoSeen(new Date());
      }
    }
    hasLoaded.current = true;
  }, [
    isPostPublish,
    deviation,
    hasCoreMembership,
    user,
    lastBoostPromoSeen,
    setLastBoostPromoSeen,
  ]);

  return isBoostModalOpen && deviation ? (
    <BoostRootModal
      deviation={deviation}
      onRequestClose={() => setIsBoostModalOpen(false)}
    />
  ) : null;
}
